import * as z from 'zod';

export const CreateVariantRequest = z.object({
  variant: z
    .object({
      _id: z
        .string()
        .describe('Item variant ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the item variant was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the item variant was updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Item variant name.').min(1).max(200),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      businessLocationIds: z.array(z.string()).max(100).optional(),
    })
    .describe('Item variant info.'),
});
export const CreateVariantResponse = z.object({
  _id: z
    .string()
    .describe('Item variant ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the item variant was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the item variant was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Item variant name.')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  businessLocationIds: z.array(z.string()).max(100).optional(),
});
export const GetVariantRequest = z.object({
  variantId: z
    .string()
    .describe('ID of the item variant to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetVariantResponse = z.object({
  _id: z
    .string()
    .describe('Item variant ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the item variant was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the item variant was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Item variant name.')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  businessLocationIds: z.array(z.string()).max(100).optional(),
});
export const ListVariantsRequest = z.object({
  options: z
    .object({
      variantIds: z.array(z.string()).max(500).optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .max(500)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('The metadata of the paginated results.')
        .optional(),
    })
    .optional(),
});
export const ListVariantsResponse = z.object({
  variants: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item variant ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the item variant was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item variant was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Item variant name.')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        businessLocationIds: z.array(z.string()).max(100).optional(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const UpdateVariantRequest = z.object({
  _id: z
    .string()
    .describe('Item variant ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  variant: z
    .object({
      _id: z
        .string()
        .describe('Item variant ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the item variant was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the item variant was updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Item variant name.')
        .min(1)
        .max(200)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      businessLocationIds: z.array(z.string()).max(100).optional(),
    })
    .describe('Item variant to update.'),
});
export const UpdateVariantResponse = z.object({
  _id: z
    .string()
    .describe('Item variant ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the item variant was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the item variant was updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Item variant name.')
    .min(1)
    .max(200)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  businessLocationIds: z.array(z.string()).max(100).optional(),
});
export const BulkCreateVariantsRequest = z.object({
  variants: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item variant ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the item variant was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item variant was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Item variant name.')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        businessLocationIds: z.array(z.string()).max(100).optional(),
      })
    )
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to receive the created item variants in the response.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateVariantsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for creation of item variants.')
          .optional(),
        variant: z
          .object({
            _id: z
              .string()
              .describe('Item variant ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the item variant was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item variant was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Item variant name.')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            businessLocationIds: z.array(z.string()).max(100).optional(),
          })
          .describe(
            'Created item variants. Only returned if the `returnEntity` value is `true`.'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const BulkUpdateVariantsRequest = z.object({
  variants: z
    .array(
      z.object({
        variant: z
          .object({
            _id: z
              .string()
              .describe('Item variant ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the item variant was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item variant was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Item variant name.')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            businessLocationIds: z.array(z.string()).max(100).optional(),
          })
          .describe('Item variant to update.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to receive the updated item variants in the response.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateVariantsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for creation of item variants.')
          .optional(),
        variant: z
          .object({
            _id: z
              .string()
              .describe('Item variant ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the item variant was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the item variant was updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Item variant name.')
              .min(1)
              .max(200)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            businessLocationIds: z.array(z.string()).max(100).optional(),
          })
          .describe(
            'Created item variants. Only returned if the `returnEntity` value is `true`.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const DeleteVariantRequest = z.object({
  variantId: z
    .string()
    .describe('Item variant ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteVariantResponse = z.object({});
export const QueryVariantsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $gt: z.string(),
              $gte: z.string(),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z.array(z.object({})).optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryVariantsResponse = z.object({
  variants: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item variant ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the item variant is updated. To prevent conflicting changes, the current revision must be passed when updating the item variant. Ignored when creating an item variant.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the item variant was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the item variant was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Item variant name.')
          .min(1)
          .max(200)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        businessLocationIds: z.array(z.string()).max(100).optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata of the paginated results.')
    .optional(),
});
export const CountVariantsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe('Filter for counting variants.')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CountVariantsResponse = z.object({
  count: z.number().int().describe('Counted variants.').optional(),
});
export const BulkDeleteVariantsRequest = z.object({
  ids: z.array(z.string()).max(200),
});
export const BulkDeleteVariantsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for variants deletion.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
